import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import ChatHeader from './ChatHeader';
import DateSeparator from './DateSeparator';
import { Contact, Message } from '../../types/chat';
import { Loader2 } from 'lucide-react'; // Added Loader2

interface ChatAreaProps {
  contact: Contact | null;
  messages: Message[];
  onSendMessage: (channelId: string,content: string, mediaFile?: File, quotedMsgId?: string | null) => void;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  // Add new props
  quotedMsgId: string | null;
  setQuotedMsgId: (id: string | null) => void;
}

const ChatArea: React.FC<ChatAreaProps> = ({
  contact,
  messages,
  onSendMessage, // This is now the full sendMessage from useChat
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  quotedMsgId,    // New prop
  setQuotedMsgId  // New prop
}) => {
  const { t } = useTranslation();
  // Find the quoted message content
  console.log("messages", messages);
  const quotedMessage = messages.find(m => m.id === quotedMsgId);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollHeightRef = useRef<number>(0); // To store previous scrollHeight
  const shouldScrollToBottomRef = useRef<boolean>(true); // To auto-scroll on new own messages

  useEffect(() => {
    // When contact changes, reset scroll and auto-scroll behavior
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    shouldScrollToBottomRef.current = true;
  }, [contact]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      if (shouldScrollToBottomRef.current) {
        // Auto-scroll to bottom for new messages sent by user or first load
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        shouldScrollToBottomRef.current = false;
      } else if (isFetchingNextPage) {
        // Maintain scroll position when loading older messages
        // scrollTop should be (newScrollHeight - oldScrollHeight) + oldScrollTop
        // but since we are prepending, it's simpler:
        const newScrollHeight = messagesContainerRef.current.scrollHeight;
        messagesContainerRef.current.scrollTop += newScrollHeight - (scrollHeightRef.current || 0);

      }
      scrollHeightRef.current = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isFetchingNextPage]);


  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop } = messagesContainerRef.current;
      // Fetch more when near top (e.g., scrollTop is less than 50px)
      if (scrollTop < 50 && hasNextPage && !isFetchingNextPage && !isLoading) {
        scrollHeightRef.current = messagesContainerRef.current.scrollHeight; // Store old scroll height
        fetchNextPage();
      }
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  // This function is passed to MessageInput. MessageInput will call it with all three args.
  const handleSendMessageForInput = (channelId: string, content: string, mediaFile?: File, msgInputQuotedId?: string | null) => {
    // // msgInputQuotedId is the quotedMsgId that MessageInput was rendered with.
    onSendMessage(channelId, content, mediaFile, msgInputQuotedId);
    shouldScrollToBottomRef.current = true;
    // Optional: Clear the quotedMsgId after sending
    setQuotedMsgId(null);
  };

  const handleQuoteMessage = (messageId: string) => {
    setQuotedMsgId(messageId);
  };

  // Helper function to check if two dates are on the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  // Render messages with date separators
  const renderMessagesWithDateSeparators = () => {
    const elements: JSX.Element[] = [];
    let lastDate: Date | null = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.rawTimestamp);
      
      // Insert date separator if this is a new day
      if (!lastDate || !isSameDay(lastDate, messageDate)) {
        elements.push(
          <DateSeparator key={`date-${message.id}-${index}`} date={messageDate} />
        );
        lastDate = messageDate;
      }

      // Add the message bubble
      elements.push(
        <MessageBubble
          key={message.id}
          message={message}
          onQuoteMessage={handleQuoteMessage}
        />
      );
    });

    return elements;
  };

  if (!contact && !isLoading) { // Show placeholder if no contact selected and not initially loading contacts
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-400 text-2xl">💬</span>
          </div>
          <h2 className="text-xl font-medium text-gray-800 mb-2">{t('welcomeToMessaging')}</h2>
          <p className="text-gray-600">{t('selectContactToStartChatting')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50">
      {contact && <ChatHeader contact={contact} />}
      
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-2"
      >
        {isFetchingNextPage && (
          <div className="text-center py-2">
            <Loader2 className="animate-spin h-5 w-5 mx-auto text-gray-500" />
            <p className="text-sm text-gray-500">{t('loadingOlderMessages')}</p>
          </div>
        )}

        {isLoading && messages.length === 0 && contact && (
           <div className="text-center py-10">
             <Loader2 className="animate-spin h-8 w-8 mx-auto text-gray-500 mb-2" />
             <p className="text-gray-600">{t('loadingMessages')}</p>
           </div>
        )}

        {!isLoading && messages.length === 0 && contact && !isFetchingNextPage && (
          <div className="text-center py-10">
            <p className="text-gray-600">{t('noMessagesYet')}</p>
          </div>
        )}
        
        {/* Static end-to-end encryption message - this could be part of initial messages or a fixed banner */}
        {messages.length > 0 && (
            <div className="bg-yellow-100 border-l-4 border-yellow-400 p-3 my-2 rounded-r-lg mx-auto max-w-md text-center">
                <p className="text-sm text-yellow-800">
                    {t('messagesEndToEndEncrypted')}
                </p>
            </div>
        )}

        {renderMessagesWithDateSeparators()}
      </div>

      {contact && (() => {
        // isActive ya refleja el estado real del backend (permite enviar durante
        // el período de gracia posterior al vencimiento) — no se vuelve a
        // combinar con isExpired acá, que solo indica que pasó la fecha, sin
        // saber si el plan sigue en gracia o ya está suspendido de verdad.
        const isPlanOk = contact.channelPlanStatus?.isActive;
        const messageInputDisabled = !contact || contact.id === '' || isLoading || !isPlanOk;

        return (
          <>
            {!isPlanOk && (
              <div className="p-4 bg-red-100 border-t border-red-200 text-center text-red-700">
                {t('subscriptionExpired')}
              </div>
            )}
            <MessageInput
              onSendMessage={handleSendMessageForInput} // Pass the updated handler
              disabled={messageInputDisabled}
              quotedMsgId={quotedMsgId} // Pass the current quotedMsgId
              setQuotedMsgId={setQuotedMsgId} // Pass the setter function
              quotedMessageDetails={quotedMessage || null} // Pass the full quoted message object
              channelId={contact.channelId.toString()} // Ensure channelId is string
            />
          </>
        );
      })()}
    </div>
  );
};

export default ChatArea;